import React from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import { pushApi } from "../../api/push";
import { subscribePanelPush } from "../../utils/webPushClient";
import {
  OWNER_PUSH_DEFAULT_PREFERENCES,
  adoptionCadenceAllows,
  currentNotificationPermission,
  getPanelInstallationId,
  resolveProjectPushState,
} from "../../utils/projectPushState";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

function humanActivationError() {
  return "No pudimos activar las notificaciones desde Inicio. Podés reintentarlo desde Configuración.";
}

export default function NotificationActivationNudge({ projectId, suppressed = false }) {
  const installationId = React.useMemo(() => getPanelInstallationId(), []);
  const [status, setStatus] = React.useState(null);
  const [pushState, setPushState] = React.useState(null);
  const [adoption, setAdoption] = React.useState(null);
  const [visible, setVisible] = React.useState(false);
  const [working, setWorking] = React.useState(false);
  const [feedback, setFeedback] = React.useState("");
  const exposureAttemptRef = React.useRef("");

  const loadState = React.useCallback(async () => {
    if (!projectId || !installationId || suppressed) {
      setVisible(false);
      return null;
    }
    try {
      const response = await pushApi.status(projectId, { installationId });
      const resolved = await resolveProjectPushState(response);
      setStatus(response);
      setPushState(resolved);
      setAdoption(response?.adoption || null);
      if (resolved.activeForCurrentProject || resolved.blocked || !resolved.supported) {
        setVisible(false);
      }
      return { response, resolved };
    } catch {
      setVisible(false);
      return null;
    }
  }, [installationId, projectId, suppressed]);

  React.useEffect(() => {
    setStatus(null);
    setPushState(null);
    setAdoption(null);
    setVisible(false);
    setFeedback("");
    exposureAttemptRef.current = "";
    loadState();
  }, [loadState]);

  const eligible = Boolean(
    !suppressed &&
    status?.memberActive === true &&
    pushState?.eligibleToActivate &&
    adoptionCadenceAllows(adoption),
  );

  React.useEffect(() => {
    if (!eligible || visible || working || !projectId || !installationId) return;
    const exposureKey = `${projectId}:${installationId}:${adoption?.snoozedUntil || "ready"}`;
    if (exposureAttemptRef.current === exposureKey) return;
    exposureAttemptRef.current = exposureKey;

    let cancelled = false;
    pushApi.adoption(projectId, {
      installationId,
      event: "shown",
      surface: "project_home",
    }).then((response) => {
      if (cancelled) return;
      setAdoption(response?.adoption || adoption);
      if (response?.recorded === true) setVisible(true);
    }).catch(() => {
      if (!cancelled) setVisible(false);
    });

    return () => {
      cancelled = true;
    };
  }, [adoption, eligible, installationId, projectId, visible, working]);

  React.useEffect(() => {
    if (!feedback) return undefined;
    const timer = window.setTimeout(() => setFeedback(""), 4500);
    return () => window.clearTimeout(timer);
  }, [feedback]);

  const dismiss = async (reason) => {
    setVisible(false);
    try {
      const response = await pushApi.adoption(projectId, {
        installationId,
        event: "dismissed",
        reason,
        surface: "project_home",
      });
      setAdoption(response?.adoption || adoption);
    } catch {
      // El cierre sigue siendo local a esta exposición; no reabrimos el bloque por un fallo de red.
    }
  };

  const recordPermissionResult = async (result) => {
    try {
      const response = await pushApi.adoption(projectId, {
        installationId,
        event: "permission_result",
        result,
        surface: "project_home",
      });
      setAdoption(response?.adoption || adoption);
    } catch {
      // La suscripción real sigue siendo la autoridad aunque falle la telemetría.
    }
  };

  const activate = async () => {
    if (working) return;
    setWorking(true);
    setFeedback("");
    try {
      try {
        await pushApi.adoption(projectId, {
          installationId,
          event: "accepted",
          surface: "project_home",
        });
      } catch {
        // La telemetría/cadencia nunca debe impedir una activación voluntaria real.
      }

      const subscription = await subscribePanelPush(status?.publicKey);
      await pushApi.subscribe(projectId, subscription.toJSON(), OWNER_PUSH_DEFAULT_PREFERENCES);

      const refreshed = await pushApi.status(projectId, { installationId });
      const resolved = await resolveProjectPushState(refreshed);
      if (!resolved.activeForCurrentProject) throw new Error("PUSH_ASSOCIATION_INCOMPLETE");

      await recordPermissionResult("granted");
      setStatus(refreshed);
      setPushState(resolved);
      setVisible(false);
      setFeedback("Notificaciones activadas");
    } catch {
      const permission = currentNotificationPermission();
      if (permission === "default") {
        await recordPermissionResult("default");
        setVisible(false);
      } else if (permission === "denied") {
        await recordPermissionResult("denied");
        setVisible(false);
      } else if (permission === "unsupported") {
        await recordPermissionResult("unsupported");
        setVisible(false);
      } else {
        await recordPermissionResult("error");
        setVisible(false);
        setFeedback(humanActivationError());
      }
      await loadState();
    } finally {
      setWorking(false);
    }
  };

  if (feedback) {
    return <Alert severity={feedback === "Notificaciones activadas" ? "success" : "warning"} sx={{ borderRadius: 3 }}>{feedback}</Alert>;
  }
  if (!visible) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: { xs: 2.7, sm: 3.2 },
        border: "1px solid rgba(105,87,232,.18)",
        bgcolor: "rgba(105,87,232,.045)",
        p: { xs: 1.15, sm: 1.35 },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ width: 38, height: 38, borderRadius: 2.1, display: "grid", placeItems: "center", flexShrink: 0, bgcolor: "#fff", color: "#6957E8" }}>
          <NotificationsActiveRoundedIcon sx={{ fontSize: 21 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ color: BRAND.dark, fontWeight: 1000, fontSize: { xs: ".88rem", sm: ".96rem" }, lineHeight: 1.2 }}>
            ¿Querés enterarte de lo importante?
          </Typography>
          <Typography sx={{ mt: .3, color: "rgba(25,36,47,.6)", fontSize: { xs: ".7rem", sm: ".76rem" }, lineHeight: 1.4 }}>
            Activá avisos para recibir movimientos y alertas importantes de este proyecto.
          </Typography>
        </Box>
        <IconButton
          size="small"
          aria-label="Cerrar invitación a notificaciones"
          disabled={working}
          onClick={() => dismiss("close")}
          sx={{ mt: -.45, mr: -.45, color: "rgba(25,36,47,.5)" }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={.75} sx={{ mt: 1 }}>
        <Button
          variant="contained"
          disabled={working}
          onClick={activate}
          sx={{ minHeight: 44, borderRadius: 999, textTransform: "none", fontWeight: 1000, bgcolor: BRAND.orange, "&:hover": { bgcolor: BRAND.orange, filter: "brightness(.94)" } }}
        >
          {working ? "Activando…" : "Activar notificaciones"}
        </Button>
        <Button
          variant="text"
          disabled={working}
          onClick={() => dismiss("later")}
          sx={{ minHeight: 44, borderRadius: 999, textTransform: "none", fontWeight: 900, color: "rgba(25,36,47,.66)" }}
        >
          Ahora no
        </Button>
      </Stack>
    </Paper>
  );
}
